import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Card, Space, Button, DatePicker, Select, Upload, Tabs, message, InputNumber, Form, Modal } from 'antd';
import BillDetailsModal from './BillDetailsModal';
import { FileExcelOutlined } from '@ant-design/icons';
import './BillVerification.css';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const BillVerification = () => {
  const [activeTab, setActiveTab] = useState('profit');
  const [courierData, setCourierData] = useState([]);
  const [customsData, setCustomsData] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configuration, setConfiguration] = useState({ price_per_piece: 0, price_per_kg: 0 });
  const [configForm] = Form.useForm();
  const [dateRange, setDateRange] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedBillType, setSelectedBillType] = useState(null);
  const [selectedBillData, setSelectedBillData] = useState(null);
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [duplicateRecords, setDuplicateRecords] = useState([]);

  // Fetch courier bills data
  const fetchCourierBills = async () => {
    setLoading(true);
    try {
      let url = '/api/users/bills/courier/list/';
      if (dateRange) {
        url += `?start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`;
      }
      if (selectedCustomer !== 'all') {
        url += `${dateRange ? '&' : '?'}customer=${selectedCustomer}`;
      }
      
      const response = await axios.get(url);
      setCourierData(response.data.data);
    } catch (error) {
      message.error('获取快递账单数据失败');
      console.error('Error fetching courier bills:', error);
    }
    setLoading(false);
  };

  // Fetch customs bills data
  const fetchCustomsBills = async () => {
    setLoading(true);
    try {
      let url = '/api/users/bills/customs/list/';
      if (dateRange) {
        url += `?start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`;
      }
      if (selectedCountry !== 'all') {
        url += `${dateRange ? '&' : '?'}country=${selectedCountry}`;
      }
      
      const response = await axios.get(url);
      setCustomsData(response.data.data);
    } catch (error) {
      message.error('获取清关账单数据失败');
      console.error('Error fetching customs bills:', error);
    }
    setLoading(false);
  };

  // Fetch profit data
  const fetchProfitData = async () => {
    setLoading(true);
    try {
      let url = '/api/users/bills/profit/list/';
      if (dateRange) {
        url += `?start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`;
      }
      
      const response = await axios.get(url);
      setProfitData(response.data.data);
    } catch (error) {
      message.error('获取利润数据失败');
      console.error('Error fetching profit data:', error);
    }
    setLoading(false);
  };

  // Fetch configuration
  const fetchConfiguration = async () => {
    try {
      const response = await axios.get('/api/users/configuration/');
      setConfiguration(response.data);
      configForm.setFieldsValue(response.data);
    } catch (error) {
      message.error('获取配置失败');
    }
  };

  // Update configuration
  const updateConfiguration = async (values) => {
    try {
      const response = await axios.post('/api/users/configuration/update/', values);
      setConfiguration(response.data);
      message.success('配置更新成功');
    } catch (error) {
      message.error('更新配置失败');
    }
  };

  // Effect to fetch configuration on mount
  useEffect(() => {
    fetchConfiguration();
  }, []);

  // Effect to fetch data when filters change
  useEffect(() => {
    if (activeTab === 'courier') {
      fetchCourierBills();
    } else if (activeTab === 'customs') {
      fetchCustomsBills();
    } else if (activeTab === 'profit') {
      fetchProfitData();
    }
  }, [activeTab, dateRange, selectedCountry, selectedCustomer]);

  // Profit table columns
  const profitColumns = [
    {
      title: '提单号',
      dataIndex: 'masterNumber',
      key: 'masterNumber',
    },
    {
      title: '税金成本',
      dataIndex: 'totalTaxCost',
      key: 'totalTaxCost',
      render: (amount) => `¥${amount.toFixed(2)}`,
    },
    {
      title: '快递成本',
      dataIndex: 'expressCost',
      key: 'expressCost',
      render: (amount) => `¥${amount.toFixed(2)}`,
    },
    {
      title: '总成本',
      key: 'totalCost',
      render: (_, record) => `¥${(record.totalTaxCost + record.expressCost).toFixed(2)}`,
    },
    {
      title: '分单数',
      dataIndex: 'houseBillCount',
      key: 'houseBillCount',
    },
    {
      title: '计费重量(kg)',
      dataIndex: 'billingWeight',
      key: 'billingWeight',
      render: (weight) => weight ? weight.toFixed(2) : '-',
    },
    {
      title: '单件成本',
      key: 'unitCost',
      render: (_, record) => {
        const totalCost = record.totalTaxCost + record.expressCost;
        return record.houseBillCount ? `¥${(totalCost / record.houseBillCount).toFixed(2)}` : '-';
      },
    },
    {
      title: '毛利润',
      key: 'grossProfit',
      render: (_, record) => {
        const totalCost = record.totalTaxCost + record.expressCost;
        if (!record.houseBillCount) return '-';
        
        let grossProfit;
        if (record.billingWeight >= 1000) {
          grossProfit = record.houseBillCount * configuration.price_per_kg - totalCost;
        } else {
          grossProfit = record.houseBillCount * configuration.price_per_piece - totalCost;
        }
        return `¥${grossProfit.toFixed(2)}`;
      },
    },
    {
      title: '单件利润',
      key: 'unitProfit',
      render: (_, record) => {
        const totalCost = record.totalTaxCost + record.expressCost;
        if (!record.houseBillCount || !record.billingWeight) return '-';
        
        let grossProfit;
        if (record.billingWeight >= 1000) {
          grossProfit = record.houseBillCount * configuration.price_per_kg - totalCost;
          return `¥${(grossProfit / record.billingWeight).toFixed(2)}`;
        } else {
          grossProfit = record.houseBillCount * configuration.price_per_piece - totalCost;
          return `¥${(grossProfit / record.houseBillCount).toFixed(2)}`;
        }
      },
    }
  ];

  // File upload handlers
  const handleCourierUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      message.loading('正在处理快递账单文件...', 0);
      const response = await axios.post('/api/users/bills/courier/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      message.destroy();
      message.success('快递账单导入成功');
      
      if (response.data.duplicates > 0) {
        setDuplicateRecords(response.data.duplicate_records);
        setDuplicateModalVisible(true);
      }
      fetchCourierBills();
    } catch (error) {
      message.destroy();
      message.error('处理文件时出错: ' + (error.response?.data?.error || error.message));
    }
    return false;
  };

  const handleCustomsUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      message.loading('正在处理清关账单文件...', 0);
      await axios.post('/api/users/bills/customs/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      message.destroy();
      message.success('清关账单导入成功');
      fetchCustomsBills();
    } catch (error) {
      message.destroy();
      message.error('处理文件时出错: ' + (error.response?.data?.error || error.message));
    }
    return false;
  };

  // Fetch bill details
  const fetchBillDetails = async (id, type) => {
    try {
      const response = await axios.get(`/api/users/bills/${type}/${id}/detail/`);
      setSelectedBillData(response.data);
      setSelectedBillType(type);
      setModalVisible(true);
    } catch (error) {
      message.error(`获取${type === 'courier' ? '快递' : '清关'}账单详情失败`);
      console.error('Error fetching bill details:', error);
    }
  };

  // Handle row clicks
  const handleRowClick = (record, type) => {
    fetchBillDetails(record.id, type);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedBillData(null);
    setSelectedBillType(null);
  };

  // Handle filter changes
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleCustomerChange = (value) => {
    setSelectedCustomer(value);
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
  };

  // Courier bills table columns
  const courierColumns = [
    {
      title: '运单号码',
      dataIndex: 'waybillNumber',
      key: 'waybillNumber',
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: '客户',
      dataIndex: 'customer',
      key: 'customer',
    },
    {
      title: '订单数量',
      dataIndex: 'orderCount',
      key: 'orderCount',
    },
    {
      title: '应付金额',
      dataIndex: 'payableAmount',
      key: 'payableAmount',
      render: (amount) => `¥${amount.toFixed(2)}`,
    }
  ];

  // Customs bills table columns
  const customsColumns = [
    {
      title: '主单号',
      dataIndex: 'masterNumber',
      key: 'masterNumber',
    },
    {
      title: '国别',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: '到港日期',
      dataIndex: 'arrivalDate',
      key: 'arrivalDate',
    },
    {
      title: '件数',
      dataIndex: 'packageCount',
      key: 'packageCount',
    },
    {
      title: '分单数',
      dataIndex: 'houseBillCount',
      key: 'houseBillCount',
    },
    {
      title: '毛重(kg)',
      dataIndex: 'grossWeight',
      key: 'grossWeight',
      render: (weight) => weight.toFixed(2),
    },
    {
      title: '计费重量(kg)',
      dataIndex: 'billingWeight',
      key: 'billingWeight',
      render: (weight) => weight.toFixed(2),
    },
    {
      title: '合计金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => `¥${amount.toFixed(2)}`,
    },
    {
      title: '验证数',
      dataIndex: 'verificationCount',
      key: 'verificationCount',
    }
  ];

  return (
    <Card title="账单核算">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space size="large" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between' }}>
            <Space size="large">
              <Upload
                beforeUpload={handleCustomsUpload}
                showUploadList={false}
              >
                <Button icon={<FileExcelOutlined />}>上传清关账单</Button>
              </Upload>
              <Upload
                beforeUpload={handleCourierUpload}
                showUploadList={false}
              >
                <Button icon={<FileExcelOutlined />}>上传快递账单</Button>
              </Upload>
            </Space>
            
            <Form
              form={configForm}
              layout="inline"
              onFinish={updateConfiguration}
            >
              <Form.Item
                label="单价/件"
                name="price_per_piece"
                rules={[{ required: true, message: '请输入单价/件' }]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  step={0.01}
                  style={{ width: 120 }}
                  prefix="¥"
                />
              </Form.Item>
              <Form.Item
                label="单价/KG"
                name="price_per_kg"
                rules={[{ required: true, message: '请输入单价/KG' }]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  step={0.01}
                  style={{ width: 120 }}
                  prefix="¥"
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  保存配置
                </Button>
              </Form.Item>
            </Form>
          </Space>

          <Tabs activeKey={activeTab} onChange={setActiveTab} size="small">
          <TabPane tab="利润计算" key="profit">
            <Space style={{ marginBottom: 16 }} size="large">
              <RangePicker 
                placeholder={['开始日期', '结束日期']}
                onChange={handleDateRangeChange}
                value={dateRange}
              />
            </Space>

            <Table
              loading={loading}
              columns={profitColumns}
              dataSource={profitData}
              rowKey="id"
              size="small"
              className="compact-table"
              onRow={(record) => ({
                onClick: () => handleRowClick(record, 'profit')
              })}
              style={{
                '.ant-table-thead > tr > th': {
                  padding: '4px 8px',
                  lineHeight: '1',
                },
                '.ant-table-tbody > tr > td': {
                  padding: '2px 8px',
                  lineHeight: '1',
                }
              }}
              pagination={{
                total: profitData.length,
                pageSize: 100,
                showTotal: (total) => `共 ${total} 条`,
              }}
            />
          </TabPane>
          <TabPane tab="清关账单" key="customs">
            <Space style={{ marginBottom: 16 }} size="large">
              <RangePicker 
                placeholder={['开始日期', '结束日期']}
                onChange={handleDateRangeChange}
                value={dateRange}
              />
              <Select
                placeholder="选择国别"
                style={{ width: 200 }}
                value={selectedCountry}
                onChange={handleCountryChange}
                options={[
                  { value: 'all', label: '全部国家' },
                  { value: 'JP', label: '日本' },
                  { value: 'KR', label: '韩国' },
                ]}
              />
            </Space>

            <Table
              loading={loading}
              columns={customsColumns}
              dataSource={customsData}
              rowKey="id"
              size="small"
              className="compact-table"
              onRow={(record) => ({
                onClick: () => handleRowClick(record, 'customs')
              })}
              pagination={{
                total: customsData.length,
                pageSize: 100,
                showTotal: (total) => `共 ${total} 条`,
              }}
            />
          </TabPane>
          <TabPane tab="快递账单" key="courier">
            <Space style={{ marginBottom: 16 }} size="large">
              <RangePicker 
                placeholder={['开始日期', '结束日期']}
                onChange={handleDateRangeChange}
                value={dateRange}
              />
              <Select
                placeholder="选择客户"
                style={{ width: 200 }}
                value={selectedCustomer}
                onChange={handleCustomerChange}
                options={[
                  { value: 'all', label: '全部客户' },
                  { value: 'customerA', label: '客户A' },
                  { value: 'customerB', label: '客户B' },
                  { value: 'customerC', label: '客户C' },
                ]}
              />
            </Space>

            <Table
              loading={loading}
              columns={courierColumns}
              dataSource={courierData}
              rowKey="id"
              size="small"
              className="compact-table"
              onRow={(record) => ({
                onClick: () => handleRowClick(record, 'courier')
              })}
              pagination={{
                total: courierData.length,
                pageSize: 100,
                showTotal: (total) => `共 ${total} 条`,
              }}
            />
          </TabPane>
        </Tabs>
      </Space>
      <BillDetailsModal
        visible={modalVisible}
        onClose={handleModalClose}
        data={selectedBillData}
        type={selectedBillType}
      />
      <Modal
        title="以下运单号码已存在"
        open={duplicateModalVisible}
        onCancel={() => setDuplicateModalVisible(false)}
        footer={null}
        centered
        width={600}
        maskClosable={false}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {duplicateRecords.map((record, index) => (
            <div 
              key={index} 
              style={{ 
                padding: '8px',
                borderBottom: index < duplicateRecords.length - 1 ? '1px solid #f0f0f0' : 'none',
                lineHeight: '1.5'
              }}
            >
              {record.waybill_number} (第{record.row}行) - 上次上传时间: {record.created_at}
            </div>
          ))}
        </div>
      </Modal>
    </Card>
  );
};

export default BillVerification;
